import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Toaster, toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { Leaf, Calendar, Clock, IndianRupee, Zap, ArrowRight, CheckCircle2, MapPin, Sun, MessageCircle, Building2, Building, Home, Lightbulb, BatteryCharging, HelpCircle } from "lucide-react";

// ── Time slot presets (visit windows) ────────────────────────────────
const TIME_SLOTS = [
  { value: "09:00", label: "9:00 AM" },
  { value: "11:00", label: "11:00 AM" },
  { value: "14:00", label: "2:00 PM" },
  { value: "16:00", label: "4:00 PM" },
  { value: "18:00", label: "6:00 PM" },
];

const PROPERTY_TYPES = [
  { value: "residential", label: "Residential", Icon: Home },
  { value: "commercial", label: "Commercial", Icon: Building2 },
  { value: "housing_society", label: "Housing Society", Icon: Building },
  { value: "ev_charger", label: "EV Charger", Icon: Zap },
  { value: "street_light", label: "Street Light", Icon: Lightbulb },
  { value: "battery_storage", label: "Battery Storage", Icon: BatteryCharging },
  { value: "others", label: "Others", Icon: HelpCircle },
];
const EPC_TYPES = new Set(["residential", "commercial", "housing_society"]);

function fmt(n) {
  return Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });
}

// today's ISO date for min attribute
function todayISO() {
  const t = new Date();
  t.setMinutes(t.getMinutes() - t.getTimezoneOffset());
  return t.toISOString().slice(0, 10);
}

export default function PublicSurvey() {
  const [params] = useSearchParams();

  const [form, setForm] = useState({
    name: params.get("name") || "",
    phone: params.get("phone") || "",
    email: params.get("email") || "",
    address: "",
    customer_type: "residential",
    preferred_visit_date: "",
    preferred_visit_time: "11:00",
    monthly_bill: "",
    units_per_month: "",
    lead_category_other_text: "",
  });

  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  // A/B test: persist variant per browser so user sees stable copy across refresh.
  // Variant is sent back to backend via source_campaign for attribution.
  const [ctaVariant] = useState(() => {
    try {
      const k = "blg_cta_variant";
      let v = localStorage.getItem(k);
      if (!v) {
        v = Math.random() < 0.5 ? "A" : "B";
        localStorage.setItem(k, v);
      }
      return v === "B" ? "B" : "A";
    } catch {
      return "A";
    }
  });
  const ctaCopy = ctaVariant === "B" ? "Get my free site evaluation" : "Book my site visit";

  // Auto-derive units from bill if user hasn't manually set it
  const [autoUnits, setAutoUnits] = useState(true);
  useEffect(() => {
    if (autoUnits && form.monthly_bill) {
      const u = Math.round(Number(form.monthly_bill) / 8);
      setForm((f) => ({ ...f, units_per_month: String(u) }));
    }
  }, [form.monthly_bill, autoUnits]);

  const estimatedKw = useMemo(() => {
    const units = Number(form.units_per_month) || 0;
    return Math.max(0, Math.round((units / 120) * 10) / 10);
  }, [form.units_per_month]);

  async function submit(e) {
    e.preventDefault();
    if (!form.preferred_visit_date) {
      toast.error("Pick a preferred visit date");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        name: form.name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim().toLowerCase(),
        address: form.address.trim(),
        customer_type: form.customer_type,
        lead_category: form.customer_type,
        lead_category_other_text: form.customer_type === "others" ? form.lead_category_other_text : undefined,
        preferred_visit_date: form.preferred_visit_date,
        preferred_visit_time: form.preferred_visit_time,
        monthly_bill: Number(form.monthly_bill || 0),
        units_per_month: Number(form.units_per_month || 0),
        source_campaign:
          (params.get("utm") || params.get("campaign") || "")
            ? `${params.get("utm") || params.get("campaign")} · cta_${ctaVariant}`
            : `cta_${ctaVariant}`,
      };
      const { data } = await api.post("/public/survey-lead", payload);
      setDone(data);
      toast.success("Details Submitted");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  }

  // ── DONE SCREEN ────────────────────────────────────────────────────
  if (done) {
    return (
      <div className="min-h-screen bg-[#F2F4EE]">
        <div className="mobile-frame bg-white" data-testid="survey-success">
          <div className="px-6 pt-12 pb-10 text-center relative overflow-hidden">
            <div className="absolute -top-24 -right-12 w-56 h-56 rounded-full bg-[#7DD181]/40 blur-3xl"></div>
            <div className="relative">
              <div className="w-16 h-16 rounded-full bg-[#1F4E2C] mx-auto flex items-center justify-center">
                <CheckCircle2 className="w-8 h-8 text-[#D4FF7A]" strokeWidth={2.5} />
              </div>
              <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#1F4E2C] mt-6">
                Purvodaya Solar
              </div>
              <h1 className="font-display text-4xl font-black tracking-tighter leading-none mt-3 text-[#0E2818]">
                Details
                <br /><span className="italic font-light text-[#1F4E2C]">submitted.</span>
              </h1>
              <p className="text-sm text-[#3D5A48] mt-4 max-w-sm mx-auto">
                Thank you. Our solar consultant will reach out shortly to confirm your site visit.
              </p>

              <div className="mt-8 mx-auto max-w-sm bg-[#0E2818] text-white p-5 text-left">
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#7DD181]">Your booking</div>
                <div className="mt-3 grid grid-cols-2 gap-4">
                  <div>
                    <div className="font-mono text-[9px] uppercase tracking-widest text-[#7DD181]/70">Date</div>
                    <div className="font-display text-xl font-semibold mt-1">
                      {new Date(done.preferred_visit_date).toLocaleDateString("en-IN", {
                        day: "numeric", month: "short",
                      })}
                    </div>
                  </div>
                  <div>
                    <div className="font-mono text-[9px] uppercase tracking-widest text-[#7DD181]/70">Time</div>
                    <div className="font-display text-xl font-semibold mt-1">{done.preferred_visit_time}</div>
                  </div>
                </div>
                <div className="mt-4 pt-4 border-t border-[#1F4E2C] font-mono text-[10px] uppercase tracking-widest text-[#7DD181]/70">
                  Reference · <span className="text-white">{done.lead_id.slice(0, 8).toUpperCase()}</span>
                </div>
              </div>

              {/* Digital-assistant CTA — Surya */}
              {done.tracker_url && (
                <a
                  href={done.tracker_url}
                  data-testid="survey-tracker-cta"
                  className="mt-6 inline-flex items-center justify-center gap-2 bg-[#1F4E2C] text-white px-5 py-3.5 font-mono text-[11px] uppercase tracking-[0.2em] hover:bg-[#0E2818] transition-colors"
                >
                  <MessageCircle className="w-4 h-4" />
                  Track & chat with Surya
                  <ArrowRight className="w-4 h-4" />
                </a>
              )}
              <p className="mt-3 text-[11px] text-[#3D5A48]/80 max-w-sm mx-auto leading-relaxed">
                We've also sent a WhatsApp greeting to <span className="font-mono">{done.tracker_url ? "your number" : "—"}</span>. Reply anytime — Surya, your digital assistant, will help you with status updates, appointments and callbacks.
              </p>

              <div className="mt-10 font-mono text-[10px] uppercase tracking-[0.3em] text-[#3D5A48]/70 flex items-center justify-center gap-1.5">
                <Leaf className="w-3 h-3 text-[#1F4E2C]" /> Powered by Purvodaya Solar · 2026
              </div>
            </div>
          </div>
        </div>
        <Toaster position="top-center" richColors />
      </div>
    );
  }

  // ── MAIN UI ────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-[#F2F4EE]">
      <div className="mobile-frame bg-white" data-testid="survey-page">
        {/* Hero */}
        <div className="relative overflow-hidden bg-[#0E2818] text-white">
          <div className="absolute -right-16 -top-16 w-72 h-72 rounded-full bg-[#7DD181]/30 blur-3xl"></div>
          <div className="absolute right-8 -bottom-8 w-40 h-40 rounded-full bg-[#D4FF7A]/30 blur-2xl"></div>
          <div className="relative px-6 pt-8 pb-10">
            <div className="flex items-center gap-2">
              <div className="w-9 h-9 bg-[#7DD181] flex items-center justify-center">
                <Leaf className="w-5 h-5 text-[#0E2818]" strokeWidth={2.5} fill="#0E2818" />
              </div>
              <div>
                <div className="font-display text-base leading-none font-bold">Purvodaya Solar.</div>
                <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-[#7DD181] mt-0.5">
                  Solar Interest Survey
                </div>
              </div>
            </div>

            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#7DD181] mt-8 flex items-center gap-2">
              <Sun className="w-3 h-3" /> Free site visit · Zero obligation
            </div>
            <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter leading-[0.9] mt-3">
              Tell us when<br />we should
              <br /><span className="italic font-light text-[#D4FF7A]">stop by.</span>
            </h1>
            <p className="text-sm text-[#7DD181]/90 mt-4 leading-relaxed">
              A 45-min on-site evaluation — roof load, sanctioned load, sun-hours, custom design.
            </p>
          </div>
        </div>

        {/* FORM */}
        <form onSubmit={submit} className="px-6 py-7 space-y-7" data-testid="survey-form">
          {/* Section: Visit */}
          <section>
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#1F4E2C] flex items-center gap-2">
              <Calendar className="w-3 h-3" /> Section 01 — Visit
            </div>
            <h2 className="font-display text-2xl font-bold tracking-tighter mt-2 text-[#0E2818]">
              Pick a slot.
            </h2>

            <div className="mt-4 space-y-4">
              <div>
                <label className="block text-[10px] font-mono uppercase tracking-widest text-[#3D5A48] mb-1.5">
                  Preferred visit date *
                </label>
                <div className="relative">
                  <Calendar className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#1F4E2C]" />
                  <input
                    type="date"
                    required
                    min={todayISO()}
                    value={form.preferred_visit_date}
                    onChange={(e) => setForm({ ...form, preferred_visit_date: e.target.value })}
                    data-testid="survey-date"
                    className="w-full bg-white border border-[#D5DCC9] focus:border-[#1F4E2C] outline-none pl-10 pr-3 py-3 text-sm font-mono"
                  />
                </div>
              </div>

              <div>
                <label className="block text-[10px] font-mono uppercase tracking-widest text-[#3D5A48] mb-1.5">
                  Preferred visit time *
                </label>
                <div className="relative">
                  <Clock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#1F4E2C] z-10" />
                  <input
                    type="time"
                    required
                    value={form.preferred_visit_time}
                    onChange={(e) => setForm({ ...form, preferred_visit_time: e.target.value })}
                    data-testid="survey-time"
                    className="w-full bg-white border border-[#D5DCC9] focus:border-[#1F4E2C] outline-none pl-10 pr-3 py-3 text-sm font-mono"
                  />
                </div>
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {TIME_SLOTS.map((s) => (
                    <button
                      key={s.value}
                      type="button"
                      onClick={() => setForm({ ...form, preferred_visit_time: s.value })}
                      data-testid={`slot-${s.value}`}
                      className={
                        "text-[10px] font-mono px-2.5 py-1 border transition-colors " +
                        (form.preferred_visit_time === s.value
                          ? "bg-[#1F4E2C] text-white border-[#1F4E2C]"
                          : "border-[#D5DCC9] text-[#3D5A48] hover:border-[#1F4E2C]")
                      }
                    >
                      {s.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* Section: Power */}
          <section>
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#1F4E2C] flex items-center gap-2">
              <Zap className="w-3 h-3" /> Section 02 — Power
            </div>
            <h2 className="font-display text-2xl font-bold tracking-tighter mt-2 text-[#0E2818]">
              Your usage.
            </h2>

            <div className="mt-4 space-y-4">
              <div>
                <label className="block text-[10px] font-mono uppercase tracking-widest text-[#3D5A48] mb-1.5">
                  Approx. monthly electricity bill (₹) *
                </label>
                <div className="relative">
                  <IndianRupee className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#1F4E2C]" />
                  <input
                    type="number"
                    required
                    min="0"
                    inputMode="numeric"
                    value={form.monthly_bill}
                    onChange={(e) => setForm({ ...form, monthly_bill: e.target.value })}
                    data-testid="survey-bill"
                    placeholder="e.g. 5000"
                    className="w-full bg-white border border-[#D5DCC9] focus:border-[#1F4E2C] outline-none pl-10 pr-3 py-3 text-sm font-mono"
                  />
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-[10px] font-mono uppercase tracking-widest text-[#3D5A48]">
                    Units consumed / month (kWh) *
                  </label>
                  <button
                    type="button"
                    onClick={() => setAutoUnits((a) => !a)}
                    className={"text-[9px] font-mono uppercase tracking-widest " + (autoUnits ? "text-[#1F4E2C]" : "text-[#94A3B8]")}
                  >
                    {autoUnits ? "✓ Auto from bill" : "Manual"}
                  </button>
                </div>
                <div className="relative">
                  <Zap className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#1F4E2C]" />
                  <input
                    type="number"
                    required
                    min="0"
                    inputMode="numeric"
                    value={form.units_per_month}
                    onChange={(e) => {
                      setAutoUnits(false);
                      setForm({ ...form, units_per_month: e.target.value });
                    }}
                    data-testid="survey-units"
                    placeholder="e.g. 625"
                    className="w-full bg-white border border-[#D5DCC9] focus:border-[#1F4E2C] outline-none pl-10 pr-3 py-3 text-sm font-mono"
                  />
                </div>
              </div>

              {Number(form.units_per_month) > 0 && (
                <div className="bg-[#0E2818] text-white p-4 flex items-center gap-4">
                  <Leaf className="w-6 h-6 text-[#D4FF7A]" />
                  <div className="flex-1">
                    <div className="font-mono text-[10px] uppercase tracking-widest text-[#7DD181]">
                      Indicative system size
                    </div>
                    <div className="font-display text-3xl font-black text-[#D4FF7A] leading-none mt-1">
                      ~{estimatedKw} <span className="text-base text-white font-normal">kW rooftop</span>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-mono text-[10px] uppercase tracking-widest text-[#7DD181]">Save / yr</div>
                    <div className="font-display text-xl font-bold mt-1">
                      ₹{fmt(Number(form.monthly_bill || 0) * 12 * 0.9)}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </section>

          {/* Section: Contact */}
          <section>
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#1F4E2C] flex items-center gap-2">
              <MapPin className="w-3 h-3" /> Section 03 — You
            </div>
            <h2 className="font-display text-2xl font-bold tracking-tighter mt-2 text-[#0E2818]">
              Where to call.
            </h2>

            <div className="mt-4 space-y-4">
              <div>
                <label className="block text-[10px] font-mono uppercase tracking-widest text-[#3D5A48] mb-1.5">
                  Property type *
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {PROPERTY_TYPES.map((p) => (
                    <button
                      key={p.value}
                      type="button"
                      onClick={() => setForm({ ...form, customer_type: p.value })}
                      data-testid={`survey-prop-${p.value}`}
                      className={
                        "py-2.5 flex flex-col items-center gap-1 text-center font-mono text-[9.5px] uppercase tracking-widest border transition-colors " +
                        (form.customer_type === p.value
                          ? "border-[#1F4E2C] bg-[#1F4E2C]/5 text-[#0E2818]"
                          : "border-[#D5DCC9] text-[#3D5A48] hover:border-[#1F4E2C]")
                      }
                    >
                      <p.Icon className="w-3.5 h-3.5" />
                      {p.label}
                    </button>
                  ))}
                </div>
                <div className={"mt-2 inline-flex px-2 py-1 font-mono text-[9px] uppercase tracking-widest " + (EPC_TYPES.has(form.customer_type) ? "bg-sky-50 text-sky-700" : "bg-purple-50 text-purple-700")}>
                  {EPC_TYPES.has(form.customer_type) ? "Full Rooftop Installation" : "Product Enquiry"}
                </div>
              </div>

              {form.customer_type === "others" && (
                <div>
                  <label className="block text-[10px] font-mono uppercase tracking-widest text-[#3D5A48] mb-1.5">
                    Tell us what you need *
                  </label>
                  <textarea
                    required
                    rows={2}
                    value={form.lead_category_other_text}
                    onChange={(e) => setForm({ ...form, lead_category_other_text: e.target.value })}
                    data-testid="survey-other-text"
                    placeholder="e.g. Solar water heater, off-grid inverter…"
                    className="w-full bg-white border border-[#D5DCC9] focus:border-[#1F4E2C] outline-none px-3 py-3 text-sm font-mono"
                  />
                </div>
              )}

              <div>
                <label className="block text-[10px] font-mono uppercase tracking-widest text-[#3D5A48] mb-1.5">
                  Full name *
                </label>
                <input
                  type="text"
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  data-testid="survey-name"
                  className="w-full bg-white border border-[#D5DCC9] focus:border-[#1F4E2C] outline-none px-3 py-3 text-sm font-mono"
                />
              </div>

              <div>
                <label className="block text-[10px] font-mono uppercase tracking-widest text-[#3D5A48] mb-1.5">
                  Phone *
                </label>
                <input
                  type="tel"
                  required
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  data-testid="survey-phone"
                  placeholder="+91-9XXXXXXXXX"
                  className="w-full bg-white border border-[#D5DCC9] focus:border-[#1F4E2C] outline-none px-3 py-3 text-sm font-mono"
                />
              </div>

              <div>
                <label className="block text-[10px] font-mono uppercase tracking-widest text-[#3D5A48] mb-1.5">
                  Email *
                </label>
                <input
                  type="email"
                  required
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  data-testid="survey-email"
                  placeholder="you@email.com"
                  className="w-full bg-white border border-[#D5DCC9] focus:border-[#1F4E2C] outline-none px-3 py-3 text-sm font-mono"
                />
              </div>

              <div>
                <label className="block text-[10px] font-mono uppercase tracking-widest text-[#3D5A48] mb-1.5">
                  Property address
                </label>
                <textarea
                  rows={2}
                  value={form.address}
                  onChange={(e) => setForm({ ...form, address: e.target.value })}
                  data-testid="survey-address"
                  placeholder="City + locality is enough"
                  className="w-full bg-white border border-[#D5DCC9] focus:border-[#1F4E2C] outline-none px-3 py-3 text-sm font-mono"
                />
              </div>
            </div>
          </section>

          <button
            type="submit"
            disabled={submitting}
            data-testid="survey-submit"
            data-cta-variant={ctaVariant}
            className="btn-tactile w-full bg-[#1F4E2C] text-white py-4 font-mono text-xs uppercase tracking-[0.2em] hover:bg-[#0E2818] transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
          >
            {submitting ? "Sending…" : (
              <>
                {ctaCopy} <ArrowRight className="w-4 h-4" />
              </>
            )}
          </button>
          <p className="text-[10px] font-mono text-[#94A3B8] text-center -mt-3">
            By submitting you agree to be contacted by Purvodaya Solar. No spam.
          </p>
        </form>

        {/* footer */}
        <div className="px-6 py-6 border-t border-[#E2E8D8] flex items-center justify-center text-[10px] font-mono uppercase tracking-widest text-[#3D5A48]">
          <div className="flex items-center gap-1.5">
            <Leaf className="w-3 h-3 text-[#1F4E2C]" /> Powered by Purvodaya Solar · 2026
          </div>
        </div>
      </div>
      <Toaster position="top-center" richColors />
    </div>
  );
}
